// src/components/ui/DateRangePicker.jsx
import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Popover,
  ButtonGroup,
  useTheme,
  alpha,
} from '@mui/material';
import {
  DateRange as DateRangeIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';

/**
 * Date Range Picker component for selecting analytics time periods
 */
const DateRangePicker = ({
  startDate,
  endDate,
  onChange,
  fullWidth = false,
  size = 'medium',
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempDateRange, setTempDateRange] = useState({
    startDate: startDate ? new Date(startDate) : null,
    endDate: endDate ? new Date(endDate) : null,
  });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setTempDateRange({
      startDate: startDate ? new Date(startDate) : null,
      endDate: endDate ? new Date(endDate) : null,
    });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    if (tempDateRange.startDate && tempDateRange.endDate) {
      onChange(tempDateRange.startDate, tempDateRange.endDate);
    }
    handleClose();
  };

  const handleCancel = () => {
    handleClose();
  };

  const handleDateChange = (field, value) => {
    setTempDateRange((prev) => ({
      ...prev,
      [field]: value ? new Date(value) : null,
    }));
  };

  // Preset ranges
  const presetRanges = [
    { label: 'Last 7 Days', days: 7 },
    { label: 'Last 30 Days', days: 30 },
    { label: 'Last 90 Days', days: 90 },
    { label: 'Year to Date', custom: true }
  ];

  const handlePresetRange = (days) => {
    const end = new Date();
    let start;

    if (days === 'ytd') {
      // Year to date
      start = new Date(new Date().getFullYear(), 0, 1); // January 1st of current year
    } else {
      // Calculate start date based on days
      start = new Date();
      start.setDate(start.getDate() - days);
    }

    setTempDateRange({
      startDate: start,
      endDate: end,
    });
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const displayValue = startDate && endDate
    ? `${formatDate(new Date(startDate))} - ${formatDate(new Date(endDate))}`
    : 'Select date range';

  const open = Boolean(anchorEl);
  const id = open ? 'date-range-popover' : undefined;

  return (
    <>
      <Button
        aria-describedby={id}
        variant="outlined"
        onClick={handleClick}
        startIcon={<DateRangeIcon />}
        size={size}
        fullWidth={fullWidth}
        sx={{ 
          justifyContent: 'flex-start',
          px: 2,
          borderColor: theme.palette.divider,
          color: theme.palette.text.primary,
        }}
      >
        {displayValue}
      </Button>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            p: 2,
            width: 320,
          },
        }}
      >
        <Box sx={{ mb: 2 }}>
          <ButtonGroup
            variant="outlined"
            size="small"
            fullWidth
            sx={{ mb: 2 }}
          >
            {presetRanges.map((range) => (
              <Button
                key={range.label}
                onClick={() => handlePresetRange(range.custom ? 'ytd' : range.days)}
              >
                {range.label}
              </Button>
            ))}
          </ButtonGroup>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              value={tempDateRange.startDate ? tempDateRange.startDate.toISOString().split('T')[0] : ''}
              onChange={(e) => handleDateChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="End Date"
              type="date"
              value={tempDateRange.endDate ? tempDateRange.endDate.toISOString().split('T')[0] : ''}
              onChange={(e) => handleDateChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="outlined" size="small" onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            size="small" 
            onClick={handleApply}
            disabled={!tempDateRange.startDate || !tempDateRange.endDate}
          >
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default DateRangePicker;